import { useLocalization } from "@fluent/react";
import { ContentCard, Field } from "@narmi/design_system";
import { modules, useFeature } from "byzantine";

const {
  useColorModeContrast,
  useColorVisionDeficiency,
  useIsAccessibilityModesEnabled,
  useLegibilityFont,
} = modules.userAccessibilityPreferences;

type SelectOption = {
  value: string;
  label: string;
  description: string;
};

type AccessibilitySelectProps = {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
};

const AccessibilitySelect = ({
  label,
  value,
  options,
  onChange,
}: AccessibilitySelectProps) => (
  <Field.Select label={label} value={value} onChange={onChange}>
    {options.map((option) => (
      <Field.Select.Item key={option.value} value={option.value}>
        <div className="padding--y--xs padding--x--s">
          {option.label}
          <div className="fontSize--s fontColor--secondary">
            {option.description}
          </div>
        </div>
      </Field.Select.Item>
    ))}
  </Field.Select>
);

const AccessibilityPreferencesCard = () => {
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const isEnabled = useIsAccessibilityModesEnabled();
  const colorModeContrast = useColorModeContrast();
  const colorVisionDeficiency = useColorVisionDeficiency();
  const legibilityFont = useLegibilityFont();

  if (!isEnabled) {
    return null;
  }

  const contrastOptions: SelectOption[] = [
    {
      value: "system",
      label: l10n.getString("option-contrast-system-default"),
      description: l10n.getString("option-contrast-system-default-desc"),
    },
    {
      value: "less",
      label: l10n.getString("option-contrast-standard"),
      description: l10n.getString("option-contrast-standard-desc"),
    },
    {
      value: "more",
      label: l10n.getString("option-contrast-high"),
      description: l10n.getString("option-contrast-high-description"),
    },
  ];

  const colorVisionOptions: SelectOption[] = [
    {
      value: "none",
      label: l10n.getString("option-color-vision-none"),
      description: l10n.getString("option-color-vision-none-desc"),
    },
    {
      value: "red-green",
      label: l10n.getString("option-color-vision-red-green"),
      description: l10n.getString("option-color-vision-red-green-desc"),
    },
    {
      value: "tritanopia",
      label: l10n.getString("option-color-vision-tritanopia"),
      description: l10n.getString("option-color-vision-tritanopia-desc"),
    },
  ];

  const legibilityFontOptions: SelectOption[] = [
    {
      value: "default",
      label: l10n.getString("option-legibility-font-default"),
      description: l10n.getString("option-legibility-font-default-desc"),
    },
    {
      value: "atkinson-hyperlegible",
      label: l10n.getString("option-legibility-font-atkinson-hyperlegible"),
      description: l10n.getString(
        "option-legibility-font-atkinson-hyperlegible-desc"
      ),
    },
    {
      value: "open-dyslexic",
      label: l10n.getString("option-legibility-font-open-dyslexic"),
      description: l10n.getString("option-legibility-font-open-dyslexic-desc"),
    },
  ];

  return (
    <ContentCard kind="elevated">
      <h4 className="fontFamily--default fontSize--heading4 margin--bottom">
        {l10n.getString("heading-accessibility-preferences")}
      </h4>
      {featureEnabled("accessibility_mode_contrast") && (
        <AccessibilitySelect
          label={l10n.getString("label-contrast-preference")}
          value={colorModeContrast.preference}
          options={contrastOptions}
          onChange={(value) => {
            colorModeContrast.setColorModeContrast(
              value as typeof colorModeContrast.preference
            );
          }}
        />
      )}
      {featureEnabled("accessibility_mode_color_vision") && (
        <div className="margin--top">
          <AccessibilitySelect
            label={l10n.getString("label-color-vision-deficiency")}
            value={colorVisionDeficiency.preference}
            options={colorVisionOptions}
            onChange={(value) => {
              colorVisionDeficiency.setColorVisionDeficiency(
                value as typeof colorVisionDeficiency.preference
              );
            }}
          />
        </div>
      )}
      {featureEnabled("accessibility_mode_legibility_font") && (
        <div className="margin--top">
          <AccessibilitySelect
            label={l10n.getString("label-legibility-font")}
            value={legibilityFont.preference}
            options={legibilityFontOptions}
            onChange={(value) => {
              legibilityFont.setLegibilityFont(
                value as typeof legibilityFont.preference
              );
            }}
          />
        </div>
      )}
    </ContentCard>
  );
};

export default AccessibilityPreferencesCard;
